import React from 'react';
import _ from 'lodash';

import useTranslation from 'hooks/use-translation';

import { getCityToDisplay } from 'shared/helpers/address';
import { useRemoteCommerceComponents } from 'src/theme/provider/commerce-components/remote-theme';
import { useMetaInfo } from './use-meta-info';

import Meta from './meta';

// Search engines may rewrite snippets, so these focus on accurate, useful page summaries rather than keyword repetition.
export const useDescription = (variant = `default`, isEmbeddedOrStoreFront, options = {}) => {
  const { t } = useTranslation();

  const dutchieDescriptions = {
    default: t(
      'head.description.default',
      'Explore Dutchie’s cannabis marketplace and technology platform. Order products online from local {{retailerLabelPluralLower}} for pickup or delivery.',
      options
    ),
    blog: t(
      'head.description.blog',
      'Explore cannabis industry news, technology trends, company stories, and practical insights from Dutchie.'
    ),
    cities: t(
      'head.description.cities',
      'Browse cannabis {{retailerLabelPluralLower}} by city across the US and Canada. Compare local menus and order online for pickup or delivery.',
      options
    ),
    city: t(
      'head.description.city',
      'Find cannabis {{retailerLabelPluralLower}} in {{city}}, {{state}}. Browse local menus and order online for pickup, curbside service, or delivery.',
      options
    ),
    dispensary: t(
      'head.description.dispensary',
      'Browse the current menu from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. View products, prices, hours, and pickup or delivery options.',
      options
    ),
    dispensaryAllProducts: t(
      'head.description.dispensaryAllProducts',
      'Browse all cannabis products available from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. Order online for pickup or delivery.',
      options
    ),
    dispensaryBrand: t(
      'head.description.dispensaryBrand',
      'Shop {{brandName}} products available from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. View current prices and ordering options.',
      options
    ),
    dispensaryBrands: t(
      'head.description.dispensaryBrands',
      'Browse cannabis brands available from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. Compare products and order online.',
      options
    ),
    dispensaryInfo: t(
      'head.description.dispensaryInfo',
      'View {{dispensaryName}} hours, address, contact details, accepted payments, and pickup or delivery information in {{dispensaryCity}}, {{dispensaryState}}.',
      options
    ),
    dispensaryProduct: t(
      'head.description.dispensaryProduct',
      'Shop {{productName}}{{productBrandText}} at {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. View current price and pickup or delivery options.',
      options
    ),
    dispensaryProductCategories: t(
      'head.description.dispensaryProductCategories',
      'Browse cannabis product categories at {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}, including flower, edibles, vapes, and more.',
      options
    ),
    dispensaryProductCategory: t(
      'head.description.dispensaryProductCategory',
      'Shop {{productCategory}} at {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. Browse available products and order for pickup or delivery.',
      options
    ),
    dispensaryProductSubcategory: t(
      'head.description.dispensaryProductSubcategory',
      'Shop {{productSubcategory}} from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. View current products, prices, and ordering options.',
      options
    ),
    dispensarySpecial: t(
      'head.description.dispensarySpecial',
      'View {{specialName}} at {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. Check eligible products and current offer details.',
      options
    ),
    dispensarySpecials: t(
      'head.description.dispensarySpecials',
      'View current offers from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. Browse eligible cannabis products and offer details.',
      options
    ),
    dispensarySearch: t(
      'head.description.dispensarySearch',
      'Search {{dispensaryName}} for {{searchTerm}}. Browse matching products, current prices, and pickup or delivery options.',
      options
    ),
    concierge: t(
      'head.description.concierge',
      'Get personalized product guidance from the AI shopping concierge at {{dispensaryName}}.',
      options
    ),
    careers: t(
      'head.description.careers',
      'Explore career opportunities at Dutchie and help build technology that supports the legal cannabis industry.'
    ),
    about: t(
      'head.description.about',
      'Learn how Dutchie provides commerce, point-of-sale, payments, and marketplace technology for the legal cannabis industry.'
    ),
    help: t(
      'head.description.help',
      'Find answers, troubleshooting guidance, and customer support for Dutchie orders, accounts, and services.'
    ),
    home: t(
      'head.description.home',
      'Browse local cannabis menus and order products online for in-store pickup, curbside pickup, or delivery with Dutchie.',
      options
    ),
    news: t(
      'head.description.news',
      'Read Dutchie company news, announcements, and updates about our work in cannabis commerce and technology.'
    ),
    privacy: t(
      'head.description.privacy',
      'Read the Dutchie Privacy Policy to learn how we collect, use, protect, and manage personal information.'
    ),
    terms: t(
      'head.description.terms',
      'Read the Dutchie Terms of Service for information about using our websites, marketplace, products, and services.'
    ),
    dispoTerms: t(
      'head.description.dispoTerms',
      'Read the terms and conditions that apply to retailers using Dutchie products and services.'
    ),
    accessibility: t(
      'head.description.accessibility',
      'Learn about Dutchie’s commitment to digital accessibility and how to request assistance.'
    ),
    plus: t(
      'head.description.plus',
      'Discover Dutchie Plus, an enterprise cannabis e-commerce platform built for multi-location retailers.'
    ),
    isolatedCheckout: t(
      'head.description.isolatedCheckout',
      'Review and complete your online order from {{dispensaryName}}.',
      options
    ),
    messaging: t('head.description.messaging', 'View information about your Dutchie order messages.'),
    curbsideArrivals: t(
      'head.description.curbsideArrivals',
      'Let the retailer know you have arrived for your curbside Dutchie order.'
    ),
    whoops: '',
  };

  const embeddedAndStoreFrontDescriptions = {
    ...dutchieDescriptions,
    dispensary: t(
      'head.description.dispensary',
      'Browse the current menu from {{dispensaryName}} at {{dispensaryStreet}}, {{dispensaryCity}}, {{dispensaryState}}. Order online for pickup or delivery.',
      options
    ),
    dispensaryProduct: t(
      'head.description.dispensaryProduct',
      'Shop {{productName}}{{productBrandText}} at {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. View current price and ordering options.',
      options
    ),
    dispensarySpecials: t(
      'head.description.dispensarySpecials',
      'View current offers from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. Browse eligible products and offer details.',
      options
    ),
    dispensarySpecial: t(
      'head.description.dispensarySpecial',
      'View {{specialName}} at {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. Check eligible products and offer details.',
      options
    ),
    dispensaryBrands: t(
      'head.description.dispensaryBrands',
      'Browse cannabis brands available from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}} and order online.',
      options
    ),
    dispensaryBrand: t(
      'head.description.dispensaryBrand',
      'Shop {{brandName}} products available from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. View current prices and availability.',
      options
    ),
    dispensaryAllProducts: t(
      'head.description.dispensaryAllProducts',
      'Browse all cannabis products available from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}} and order online.',
      options
    ),
    dispensaryProductCategories: t(
      'head.description.dispensaryProductCategories',
      'Browse cannabis product categories available from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}.',
      options
    ),
    dispensaryProductCategory: t(
      'head.description.dispensaryProductCategory',
      'Shop {{productCategory}} available from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. View current products and prices.',
      options
    ),
    dispensaryProductSubcategory: t(
      'head.description.dispensaryProductSubcategory',
      'Shop {{productSubcategory}} available from {{dispensaryName}} in {{dispensaryCity}}, {{dispensaryState}}. View products and prices.',
      options
    ),
    dispensarySearch: t(
      'head.description.dispensarySearch',
      'Search {{dispensaryName}} for {{searchTerm}}. Browse matching products, current prices, and ordering options.',
      options
    ),
  };

  const descriptions = isEmbeddedOrStoreFront ? embeddedAndStoreFrontDescriptions : dutchieDescriptions;
  const resolvedVariant = descriptions[variant] !== undefined ? variant : `default`;

  return descriptions[resolvedVariant];
};

export default function PageDescription(props) {
  const { variant, location, isEmbeddedOrStoreFront } = props;
  const metaInfo = useMetaInfo();
  const { remoteThemeModule } = useRemoteCommerceComponents();
  const productBrandText = metaInfo.productBrand ? ` by ${metaInfo.productBrand}` : '';

  const description = useDescription(variant, isEmbeddedOrStoreFront, {
    ...metaInfo,
    productBrandText,
    productSubcategory: _.toLower(metaInfo.productSubcategory),
    productCategory: _.toLower(metaInfo.productCategory),
    city: getCityToDisplay(location),
    state: location?.state,
  });

  // The remote theme owns all description and social-description fields when this function is present.
  if (remoteThemeModule.default?.getStoreFrontMetaFields || !description) {
    return null;
  }

  return (
    <>
      <Meta property='description' content={description} />
      <Meta property='og:description' content={description} />
      <Meta property='twitter:description' content={description} />
      <Meta property='twitter:card' content='summary_large_image' />
      <Meta property='og:type' content={variant === 'dispensaryProduct' ? 'product' : 'website'} />
    </>
  );
}
